import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Grid,
  Button,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  useMediaQuery,
  TablePagination,
} from '@mui/material';
import { Visibility, Search, FilterAlt, Refresh, LocalShipping, CheckCircle, Pending, Cancel, Assignment } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getOrders } from '../store/slices/orderSlice';
import { useState, useEffect, useMemo } from 'react';

const Orders = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { orders, isLoading, error } = useSelector((state) => state.orders);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // State for filtering and pagination
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    dispatch(getOrders());
  }, [dispatch]);

  // Filter orders based on search term and status
  const filteredOrders = useMemo(() => {
    if (!orders) return [];
    
    return orders.filter(order => {
      const matchesSearch = order.orderNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (order.user?.name && order.user.name.toLowerCase().includes(searchTerm.toLowerCase()));
      
      let matchesStatus = true;
      switch (statusFilter) {
        case 'all':
          matchesStatus = true;
          break;
        case 'pending':
          matchesStatus = order.status === 'pending' || order.orderStatus === 'pending';
          break;
        case 'processing':
          matchesStatus = order.status === 'processing' || order.orderStatus === 'processing';
          break;
        case 'shipped':
          matchesStatus = order.status === 'shipped' || order.orderStatus === 'shipped';
          break;
        case 'delivered':
          matchesStatus = order.status === 'delivered' || order.orderStatus === 'delivered';
          break;
        case 'cancelled':
          matchesStatus = order.status === 'cancelled' || order.orderStatus === 'cancelled';
          break;
        default:
          matchesStatus = true;
      }
      
      return matchesSearch && matchesStatus;
    });
  }, [orders, searchTerm, statusFilter]);

  // Pagination
  const paginatedOrders = useMemo(() => {
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return filteredOrders.slice(startIndex, endIndex);
  }, [filteredOrders, page, rowsPerPage]);

  const handleRefresh = () => {
    setIsRefreshing(true);
    dispatch(getOrders()).finally(() => {
      setIsRefreshing(false);
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'confirmed': return 'info';
      case 'processing': return 'warning';
      case 'shipped': return 'warning';
      case 'delivered': return 'success';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Tab Navigation - Single Row Layout */}
      <Paper sx={{ mb: 3, borderRadius: 2, p: 2 }}>
        <Box display="flex" gap={1} flexWrap="nowrap" justifyContent="space-between">
          {[
            { id: 'all', icon: <Assignment />, label: "All Orders", color: "#ff2852" },
            { id: 'pending', icon: <Pending />, label: "Pending", color: "#f59e0b" },
            { id: 'processing', icon: <LocalShipping />, label: "Processing", color: "#3b82f6" },
            { id: 'delivered', icon: <CheckCircle />, label: "Delivered", color: "#10b981" },
            { id: 'cancelled', icon: <Cancel />, label: "Cancelled", color: "#ef4444" }
          ].map((tab) => (
            <Box
              key={tab.id}
              sx={{
                flex: '1 1 0',
                minWidth: 0,
                maxWidth: 'none'
              }}
            >
              <Paper
                onClick={() => setStatusFilter(tab.id)}
                sx={{
                  p: { xs: 1.5, sm: 2 },
                  textAlign: 'center',
                  cursor: 'pointer',
                  borderRadius: 2,
                  border: statusFilter === tab.id ? `2px solid ${tab.color}` : '1px solid #e0e0e0',
                  backgroundColor: statusFilter === tab.id ? `${tab.color}10` : '#ffffff',
                  transition: 'all 0.3s ease',
                  height: '100%',
                  minHeight: { xs: 60, sm: 70 },
                  '&:hover': {
                    backgroundColor: `${tab.color}15`,
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                  }
                }}
              >
                <Box sx={{ color: statusFilter === tab.id ? tab.color : '#666', mb: 1, fontSize: { xs: '1.4rem', sm: '1.8rem' } }}>
                  {tab.icon}
                </Box>
                <Typography 
                  variant="subtitle1" 
                  fontWeight={600}
                  sx={{ 
                    color: statusFilter === tab.id ? tab.color : '#666',
                    fontSize: { xs: '0.7rem', sm: '0.9rem' },
                    lineHeight: 1.2
                  }}
                >
                  {tab.label}
                </Typography>
              </Paper>
            </Box>
          ))}
        </Box>
      </Paper>

      {/* Header with Actions */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography
          variant="h4"
          sx={{
            color: '#000000',
            fontWeight: 700,
            pb: 2,
            borderBottom: '2px solid #ff2852',
          }}
        >
          Orders
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="contained"
            onClick={handleRefresh}
            disabled={isRefreshing}
            startIcon={<Refresh />}
            sx={{ 
              bgcolor: '#ff2852', 
              '&:hover': { bgcolor: '#d63031' },
              fontSize: { xs: '0.8rem', sm: '0.9rem' }
            }}
          >
            {isMobile ? '' : 'Refresh'}
          </Button>
        </Box>
      </Box>

      {/* Filter Controls - Mobile Optimized */}
      <Box 
        display="flex" 
        gap={{ xs: 1, sm: 2 }} 
        mb={3} 
        flexWrap="wrap" 
        alignItems="center"
        flexDirection={{ xs: 'column', sm: 'row' }}
      >
        <TextField
          size="small"
          placeholder="Search orders..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
          sx={{ 
            minWidth: { xs: '100%', sm: 250 },
            width: { xs: '100%', sm: 'auto' }
          }}
        />
        
        <Typography 
          variant="body2" 
          color="text.secondary" 
          sx={{ 
            display: { xs: 'block', sm: 'none' },
            textAlign: 'center',
            mt: { xs: 1, sm: 0 }
          }}
        >
          Showing {paginatedOrders.length} of {filteredOrders.length} orders
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Loader */}
      {(isLoading || isRefreshing) && (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={40} />
        </Box>
      )}

      {/* Orders Table - Mobile Optimized */}
      {!isLoading && !isRefreshing && (
        <>
          {paginatedOrders.length > 0 ? (
            <>
              <TableContainer 
                sx={{ 
                  border: '1px solid #eee', 
                  borderRadius: 2, 
                  mb: 2,
                  maxHeight: { xs: '400px', sm: 'none' },
                  overflow: { xs: 'auto', sm: 'visible' }
                }}
              >
                <Table size={isMobile ? "small" : "medium"}>
                  <TableHead sx={{ bgcolor: '#fafafa' }}>
                    <TableRow>
                      <TableCell 
                        sx={{ 
                          fontWeight: 700, 
                          fontSize: isMobile ? '0.75rem' : '0.875rem',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        Order #
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          fontWeight: 700, 
                          fontSize: isMobile ? '0.75rem' : '0.875rem',
                          display: { xs: 'none', sm: 'table-cell' }
                        }}
                      >
                        Customer
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          fontWeight: 700, 
                          fontSize: isMobile ? '0.75rem' : '0.875rem'
                        }}
                      >
                        Amount
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          fontWeight: 700, 
                          fontSize: isMobile ? '0.75rem' : '0.875rem'
                        }}
                      >
                        Status
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          fontWeight: 700, 
                          fontSize: isMobile ? '0.75rem' : '0.875rem',
                          display: { xs: 'none', sm: 'table-cell' }
                        }}
                      >
                        Date
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: '0.875rem' }}>
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedOrders.map((order) => (
                      <TableRow key={order._id} hover>
                        <TableCell>
                          <Typography 
                            variant="body2" 
                            fontWeight={600}
                            sx={{ fontSize: isMobile ? '0.8rem' : '0.875rem' }}
                          >
                            #{order.orderNumber}
                          </Typography>
                        </TableCell>
                        {!isMobile && (
                          <TableCell>
                            <Typography 
                              variant="body2" 
                              sx={{ fontSize: '0.875rem' }}
                            >
                              {order.user?.name || 'Guest'}
                            </Typography>
                          </TableCell>
                        )}
                        <TableCell>
                          <Typography 
                            variant="body2" 
                            fontWeight={600} 
                            color="#ff2852"
                            sx={{ fontSize: isMobile ? '0.8rem' : '0.875rem' }}
                          >
                            PKR {order.totalAmount?.toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={order.status || order.orderStatus}
                            size="small"
                            color={getStatusColor(order.status || order.orderStatus)}
                            sx={{ fontSize: '0.7rem' }}
                          />
                        </TableCell>
                        {!isMobile && (
                          <TableCell>
                            <Typography 
                              variant="body2" 
                              sx={{ fontSize: '0.875rem' }}
                            >
                              {new Date(order.createdAt).toLocaleDateString()}
                            </Typography>
                          </TableCell>
                        )}
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/orders/${order._id}`)}
                            sx={{ 
                              '&:hover': { bgcolor: '#f5f5f5' },
                              bgcolor: isMobile ? 'rgba(25, 118, 210, 0.04)' : 'transparent'
                            }}
                          >
                            <Visibility sx={{ fontSize: isMobile ? 16 : 20 }} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              
              {/* Pagination */}
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={filteredOrders.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                sx={{ 
                  '.MuiTablePagination-toolbar': { 
                    fontSize: isMobile ? '0.8rem' : '0.875rem' 
                  }
                }}
              />
            </>
          ) : (
            <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
              <Assignment sx={{ fontSize: 48, color: '#ccc', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" mb={1}>
                No Orders Found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {statusFilter === 'all' 
                  ? "Orders will appear here once customers start placing orders."
                  : `No ${statusFilter} orders found.`
                }
              </Typography>
            </Paper>
          )}
        </>
      )}
    </Box>
  );
}

export default Orders;
